import React from 'react';
import {Glob as G} from "../../libs/Glob.mjs";
import "./EditForm.css";
import TextLabel from "../../controls/TextLabel.mjs";
import {Button, ButtonGroup} from "reactstrap";
import PlayIcon from "../../media/images/PlayIcon.mjs";
import ButtonsExitReloadSave from "../../controls/ButtonsExitReloadSave.mjs";
import EditorFieldGroup from "./EditorFieldGroup.mjs";
import appLib from "../../libs/appLib.mjs";

const prefix = 'EditForm';

/** @typedef {import("./EditForm").FieldDefinition } */
/** @typedef {import("./EditForm").FieldType } */
/** @typedef {import("./EditForm").FieldDependant } */
/** @typedef {import("./EditForm").FieldDependants } */
/** @typedef {import("./EditForm").EditFormProps } */
/** @typedef {import("./EditForm").EditFormState } */

/**
 * Available field types
 * @type {FieldType[]}
 */
export const fieldTypes = [
	{ type: "group", name: "Fields group (with subnode)" },
	{ type: "iteminfo", name: "Item info", nokey:true, unique: true},
	{ type: "format", name: "Format", nokey:true },
	{ type: "text", name:"Text"},
	{ type:"radio", name:"Radio buttons"},
	{ type:"checkbox", name:"Checkbox(es)"},
	{ type:"select", name:"Select value(s)"},
	{ type:"YN", name:"YN (Yes/No)"},
	{ type:"YNA", name:"YNA (Yes/No/N.A.)"},
	{ type:"ateco", name:"Codici ATECO"},
	{ type:"TimeAmount", name:"Time amount"},
	{ type:"UnitValue", name:"Amount with unit"},
	{ type:"percent", name:"%"},
	{ type:"FromItems", name:"Select from Items"}
];

/**
 * Expand the toggles of every defined field
 * @private
 * @param {string} base
 * @return {void}
 */
const expandAllToggles = base => {
	document.getElementById(base).querySelectorAll('.MyAccordion.collapsed > .MyAccordionHeader > .MyAccordionToggler').forEach(t => {
		t.dispatchEvent( new MouseEvent('click', { view: window, bubbles: true, cancelable: true }));
	});
}

/**
 * Collapse the toggles of every defined field
 * @private
 * @param {string} base
 * @return {void}
 */
const collapseAllToggles = base => {
	document.getElementById(base).querySelectorAll('.MyAccordion.expanded > .MyAccordionHeader > .MyAccordionToggler').forEach(t => {
		t.dispatchEvent( new MouseEvent('click', { view: window, bubbles: true, cancelable: true }));
	});
}

/**
 * @private
 * @param {FieldDefinition} f
 * @return {FieldDefinition}
 */
const cleanInputFieldDefinition = f => {
	if ( ! f.uuid) f.uuid = G.generateUid('field');
	if ( f.depends) {
		if (! Array.isArray(f.depends)) f.depends = [f.depends];
		f.depends.forEach( (d,idx) => {
			if ( typeof d === 'string') f.depends[idx] = { on: d };
		})
	}
	if ( f.fields ) {
		if ( ! Array.isArray(f.fields) ) f.fields = [f.fields];
		f.fields = f.fields.map( cleanInputFieldDefinition );
	}
	return f;
};

/**
 * When fields definition is saved:
 * - Sets dependants from dependencies
 * - Refresh the list of used keys in fields
 * - sets this.state.form
 * @private
 * @function
 * @param {FieldDefinition[]} [fields] The array of fields definition
 * @param {{[p:string]:any}} [newState] Set this state as well when over
 * @return {Promise<void>}
 */
export const cleanFieldsForCommit = (fields , newState = {}) => {
	return new Promise( resolve => {
		if ( typeof fields === 'undefined') {
			fields = this.state.form ? G.clone(this.state.form) : [];
		}
		/**
		 *  Updated list of user field keys
		 *  @type { string[] }
		 */
		newState.fieldKeys = fields
			.filter( f => f?.key )
			.map( f => f.key );
		/* 1st cycle: cleanup form, reset dependants array */
		fields.forEach( f => {
			if ( f.type === 'format' ) delete f.type;
			f.dependants = [];
		});
		/* 2nd cycle: populate again dependants array */
		fields.forEach( (f,idx) => {
			if ( f.depends ) f.depends.forEach( d => {
				const dIdx = fields.findIndex( x => x.key === d?.on );
				if ( dIdx > -1 ) {
					const v = f.key || idx;
					if ( ! fields[dIdx].dependants.includes( v ) ) fields[dIdx].dependants.push(v);
				}
			})
		});
		newState.form = fields;
		resolve(newState);
	})
}


const EditForm = class extends React.Component {
	/**
	 * @constructor
	 * @param {EditFormProps} [props]
	 */
	constructor(props) {
		super(props);
		/** @type {EditFormState} */
		this.state = {
			user: G.getUserLoginName(),
			admin: G.userIsAdmin(),
			group: props.group || G.getPageParam('group'),
			lang: G.getLanguage(),
			ll: G.getLanguageLong(),
			changes: 0
		}
		this.uid = G.generateUid(prefix);
		this.initialLoad = this.initialLoad.bind(this);
		this.commit = this.commit.bind(this);
		this.setChanges = this.setChanges.bind(this);
		this.saveToServer = this.saveToServer.bind(this);
		this.setFields = this.setFields.bind(this);
		this.preSaveFilter = this.preSaveFilter.bind(this);
	}
	componentDidMount() {
		if ( ! this.state.user ) {
			G.gotoPage('Login');
			return;
		}
		if ( ! this.state.group ) {
			G.gotoHomePage();
			return;
		}
		G.trigger('pageButtons', (
			<ButtonsExitReloadSave
				key={Date.now()}
				changes={ this.state.changes }
				changesKey={"EditFormChanges"}
				exit={ true }
				reload={ this.initialLoad }
				save={ this.saveToServer }
				upload={ async ()=>{
					const data = await G.uploadJSON('.dominoFormFieldsJson');
					if ( ! data ) return;
					await this.setFields( data?.fields || [] );
					this.setChanges( 1 );
				}}
				uploadLabel={false}
				download={ () => {
					G.saveJSON(
						{ fields: this.preSaveFilter() },
						this.state.group + '.dominoFormFieldsJson'
					)
				}}
				downloadLabel={false}
			/>
		) );
		this.initialLoad();
	}
	componentWillUnmount() {
		G.trigger('pageButtons' );
		G.setStatus('fieldsOpen' );
		G.deregister(this.uid);
	}

	/**
	 * Loads (or reloads) form definition and runs firt fields parse
	 * @private
	 * @function
	 * @return {void}
	 */
	initialLoad() {
		this.setState({ info: undefined, form: undefined }, ()=>{
			G.setStatus('fieldsOpen', {});
			appLib.getFormInfo(this.state.group, true).then( res => {
				G.setStatus('pageTitle',`[${G.label('Form')}] ${res?.group?.name}`)
				/** @type { FieldDefinition[] } */
				const fields = (res?.formDefinition?.fields || []).map( cleanInputFieldDefinition );
				this.setFields( fields, { info: res } );
				this.setChanges(0);
			}).catch( (err)=>{
				console.error(err);
				G.gotoHomePage();
			});
		})
	}

	/**
	 * When fields definition is saved:
	 * - Sets dependants from dependencies
	 * - Refresh the list of used keys in fields
	 * - sets this.state.form
	 * @private
	 * @function
	 * @param {FieldDefinition[]} [fields] The array of fields definition
	 * @param {{[p:string]:any}} [newState] Set this state as well when over
	 * @return {Promise<void>}
	 */
	setFields(fields , newState = {}) {
		return new Promise( resolve => {
			if ( typeof fields === 'undefined') {
				fields = this.state.form ? G.clone(this.state.form) : [];
			}
			/**
			 *  Updated list of user field keys
			 *  @type { string[] }
			 */
			newState.fieldKeys = fields
				.filter( f => f?.key )
				.map( f => f.key );
			/* 1st cycle: cleanup form, reset dependants array */
			fields.forEach( f => {
				if ( f.type === 'format' ) delete f.type;
				f.dependants = [];
			});
			/* 2nd cycle: populate again dependants array */
			fields.forEach( (f,idx) => {
				if ( f.depends ) f.depends.forEach( d => {
					const dIdx = fields.findIndex( x => x.key === d?.on );
					if ( dIdx > -1 ) {
						const v = f.key || idx;
						if ( ! fields[dIdx].dependants.includes( v ) ) fields[dIdx].dependants.push(v);
					}
				})
			});
			newState.form = fields;
			this.setState( newState, resolve );
		})
	}

	/**
	 * Clean fields for saving or downloading
	 * @private
	 * @function
	 * @param {FieldDefinition[]} [fields] The array of fields definition
	 * @return {FieldDefinition[]}
	 */
	preSaveFilter( fields ) {
		if ( typeof fields === 'undefined') fields = this.state.form || [];
		const fs = G.clone( fields );
		fs.forEach( f => {
			if ( f.type === 'format' ) delete f.type;
			delete f.uuid;
			delete f.dependants;
			if ( f.fields ) f.fields = this.preSaveFilter( f.fields );
		});
		return fs;
	}

	/**
	 * - Commit changes in fields
	 * @private
	 * @function
	 * @param { FieldDefinition[] } [fields] Field definition object, undefined to delete it
	 * @return {void}
	 */
	commit( fields) {
		if ( typeof fields === 'undefined') fields = this.state.form || [];
		this.setFields(fields, {} );
		this.setChanges();
	}

	/**
	 * Set changes and triggers info
	 * @param {number} [qt]
	 * @return {void}
	 */
	setChanges( qt ) {
		if ( typeof qt !== "number") qt = this.state.changes + 1;
		this.setState({ changes: qt }, ()=>{
			G.trigger('EditFormChanges', qt);
		} );
	}

	/**
	 * Save changes to server
	 * @private
	 * @return {void}
	 */
	saveToServer() {
		G.api(
			'admin/setForm/'+this.state.group,
			{fields: this.preSaveFilter()})
			.then( ()=>{
				this.setChanges(0);
				appLib.clearCache();
			});
	}

	render() {
		if ( ! this.state.form ) return <div><TextLabel value={"Loading…"} /></div>;
		return(
			<div className={prefix} id={this.uid}>
				<div className={"info"}>
					<div>
					<span>
						<TextLabel value={"Items"}/>: { this.state.form.length }
					</span>
						{this.state.info?.lastUpdate ? (
							<span>
							<TextLabel
								value={"lastUpdate"}
								params={{date: (new Date(this.state.info?.lastUpdate)).toLocaleString(this.state.ll)}}
							/>
						</span>
						) : ''}
					</div>
					<div>
						<TextLabel value={"Changes"}/>: {this.state.changes}
					</div>
				</div>
				<div className={"FormControlsBar"}>
					<ButtonGroup className={"btn-icon"}>
						<Button
							disabled={ this.state.form.length === 0 }
							onClick={ ()=>{ expandAllToggles(this.uid)}}
						>
							<i>
								<PlayIcon  style={{transform:"rotate(90deg)"}}/>
							︎</i>
							<b>
								<TextLabel value={"Expand all"}/>
							</b>
						</Button>
						<Button
							disabled={this.state.form.length === 0}
							onClick={() => {
								collapseAllToggles(this.uid)
							}}
						>
							<i>
								<PlayIcon/>
								︎</i>
							<b>
								<TextLabel value={"Collapse all"}/>
							</b>
						</Button>
					</ButtonGroup>
				</div>
				<EditorFieldGroup
					fields={this.state.form}
					fieldKeys={this.state.fieldKeys}
					field={this.state.info.formDefinition }
					commit={ this.commit }
				/>
			</div>
		)
	}
}

export default EditForm;